import { useLocation } from 'react-router-dom'
import { HOME, NAV_GROUPS, findLeaf } from '@/routes/navigation'
import { REPORTS } from '@/layers/report/reportConfig'
import { Icon } from './Icon'
import type { IconName } from './icons/iconData'

interface Crumb {
  readonly label: string
  readonly routeId?: string
}

// Prototype_v2.html's own `SECTIONS` map — each group's outline icon for when the group's own
// landing page is the current screen (a different icon from `NavGroup.icon`, which is the FILLED
// sidebar version). `secaccount`'s prototype entry names `i-board`, which has no `id="i-board"`
// symbol anywhere in Prototype_v2.html's own defs — a gap in the prototype itself, not something to
// invent; `f-board` (filled, already used for this exact group in `Sidebar.tsx`) stands in for it.
const GROUP_ICON: Record<string, IconName> = {
  secdsc: 'i-shield',
  seccore: 'i-sliders',
  secanalytics: 'i-chart',
  secreporting: 'i-rocket',
  secsettings: 'i-gear',
  secaccount: 'f-board',
}

type ParentRef = { readonly kind: 'group' | 'leaf' | 'nested'; readonly routeId: string }

// Screens below a leaf (or a group, directly), reached by an in-app link rather than the sidebar
// (`ShellLayer.tsx`'s own "not top-level nav leaves" routes) — `design/27-ui-surface-and-
// navigation.md` §27.2's "Sits under" column, transcribed. `parent` is either a nav group directly,
// a nav leaf, or another key in this same map (recursed via `trailForParent`, for a route nested
// more than one level deep — e.g. a backup job sits under Backup Jobs, which sits under Jobs).
// `parent: undefined` means no parent at all (`blocked` is reached by state, never by navigating
// anywhere, per that row's own "state, not a nav destination" note).
//
// **Found live 2026-09-26 (Sanket, on the backup-job detail page): every route not in this map, or
// in `PREFIXED` below, was silently resetting the whole breadcrumb trail to Home** — this map used
// to cover only 6 routes and its own doc comment described the other ~20 as a deliberate, unfinished
// scope limit. It wasn't: nobody had come back to extend it. Extended here to the full register.
const NESTED: Record<string, { readonly parent?: ParentRef; readonly title: string; readonly icon: IconName }> = {
  'jobs/backup': { parent: { kind: 'leaf', routeId: 'jobs' }, title: 'Backup Jobs', icon: 'i-backup' },
  'jobs/restore': { parent: { kind: 'leaf', routeId: 'jobs' }, title: 'Restore Jobs', icon: 'i-restore' },
  setgeneral: { parent: { kind: 'leaf', routeId: 'setbackup' }, title: 'General Settings', icon: 'i-gear' },
  setrepos: { parent: { kind: 'leaf', routeId: 'setbackup' }, title: 'Repository Inclusion and Exclusion', icon: 'i-inout' },
  auditbb: { parent: { kind: 'leaf', routeId: 'audit' }, title: 'Bitbucket Audit Logs', icon: 'i-doc' },
  auditprod: { parent: { kind: 'leaf', routeId: 'audit' }, title: 'Product Audit Logs', icon: 'i-shield' },

  alerts: { parent: { kind: 'group', routeId: 'secdsc' }, title: 'Needs Attention', icon: 'i-alert' },
  remap: { parent: { kind: 'leaf', routeId: 'restore' }, title: 'Map People and Groups', icon: 'i-users' },
  snapshots: { parent: { kind: 'nested', routeId: 'jobs/backup' }, title: 'Restore Points', icon: 'i-restore' },
  notprotected: { parent: { kind: 'nested', routeId: 'setrepos' }, title: 'Not Protected', icon: 'i-block' },
  // `?job_id=` adds one more hop in front of this base trail when present — see `resolve()`.
  pending: { parent: { kind: 'nested', routeId: 'jobs/restore' }, title: 'Secured Values', icon: 'i-lock' },
  // Launched only from the dashboard (`ShellLayer.tsx`'s own route comment, W153) — never from auth.
  onboarding: { parent: { kind: 'leaf', routeId: 'dashboard' }, title: 'Workspace Setup', icon: 'i-gear' },
  // Reached by state (an org-blocked read), never by a click — no parent to link back to.
  blocked: { title: 'Workspace Blocked', icon: 'i-shield' },

  'repo-wz-1': { parent: { kind: 'leaf', routeId: 'restore' }, title: 'Repository Restore', icon: 'i-restore' },
  'access-wz-1': { parent: { kind: 'leaf', routeId: 'restore' }, title: 'Access & Integrations Restore', icon: 'i-restore' },
  'ci-wz-1': { parent: { kind: 'leaf', routeId: 'restore' }, title: 'CI & Deployment Restore', icon: 'i-restore' },
  'project-wz-1': { parent: { kind: 'leaf', routeId: 'restore' }, title: 'Project Policy Restore', icon: 'i-restore' },
  'settings-wz-1': { parent: { kind: 'leaf', routeId: 'restore' }, title: 'Settings & Policy Restore', icon: 'i-restore' },
  'ref-wz-1': { parent: { kind: 'leaf', routeId: 'restore' }, title: 'Ref Restore', icon: 'i-restore' },
  'review-wz-1': { parent: { kind: 'leaf', routeId: 'restore' }, title: 'Pull Request State Restore', icon: 'i-restore' },
  'wshook-wz-1': { parent: { kind: 'leaf', routeId: 'restore' }, title: 'Workspace Webhooks Restore', icon: 'i-restore' },
}

// Routes whose id carries an opaque instance id (a job/alert/workspace id) rather than being a
// fixed string — `NESTED`'s exact-match lookup can never see these, so they get their own
// prefix-matched table instead. No entry invents the instance's own name (a job's real handle, an
// alert's real id) as the current crumb's label — that real value already renders once, in the
// screen's own heading (e.g. `VBackupjob.tsx`'s `<h1>`); this only fixes the trail, which was
// resetting to Home before any of these screens had even loaded.
const PREFIXED: readonly { readonly prefix: string; readonly parent: ParentRef; readonly title: string; readonly icon: IconName }[] = [
  { prefix: 'jobs/backup/', parent: { kind: 'nested', routeId: 'jobs/backup' }, title: 'Backup Job', icon: 'i-backup' },
  { prefix: 'jobs/restore/', parent: { kind: 'nested', routeId: 'jobs/restore' }, title: 'Restore Job', icon: 'i-restore' },
  { prefix: 'firstbackup/', parent: { kind: 'group', routeId: 'secdsc' }, title: 'First Backup', icon: 'i-backup' },
  { prefix: 'lostcommits/', parent: { kind: 'nested', routeId: 'alerts' }, title: 'Lost Commits', icon: 'i-branch' },
  { prefix: 'onboarding/', parent: { kind: 'leaf', routeId: 'dashboard' }, title: 'Workspace Setup', icon: 'i-gear' },
]

// Walks one `ParentRef` all the way back to a nav group, so a route nested several levels deep
// (`pending` → `jobs/restore` → `jobs` → Core Administration) gets its full trail, not one hop.
function trailForParent(parent: ParentRef | undefined): Crumb[] {
  if (!parent) return []
  if (parent.kind === 'group') {
    const group = NAV_GROUPS.find((g) => g.routeId === parent.routeId)
    return group ? [{ label: group.title, routeId: group.routeId }] : []
  }
  if (parent.kind === 'leaf') {
    const found = findLeaf(parent.routeId)
    return found ? [{ label: found.group.title, routeId: found.group.routeId }, { label: found.leaf.title, routeId: parent.routeId }] : []
  }
  const entry = NESTED[parent.routeId]
  return entry ? [...trailForParent(entry.parent), { label: entry.title, routeId: parent.routeId }] : []
}

function resolve(pathname: string, search: string): { trail: readonly Crumb[]; current: Crumb; icon: IconName } {
  const routeId = pathname.replace(/^\//, '')

  if (!routeId || routeId === HOME.routeId) return { trail: [], current: { label: HOME.title }, icon: HOME.icon === 'f-home' ? 'i-home' : HOME.icon }

  const group = NAV_GROUPS.find((g) => g.routeId === routeId)
  if (group) return { trail: [], current: { label: group.title }, icon: GROUP_ICON[group.routeId] ?? 'i-doc' }

  const found = findLeaf(routeId)
  if (found) return { trail: [{ label: found.group.title, routeId: found.group.routeId }], current: { label: found.leaf.title }, icon: found.leaf.icon }

  const reportUnitId = routeId.startsWith('reports/') ? routeId.slice('reports/'.length) : undefined
  if (reportUnitId) {
    const report = REPORTS.find((r) => r.unitId === reportUnitId)
    const healthLeaf = findLeaf('health')
    if (report && healthLeaf) {
      return {
        trail: [
          { label: healthLeaf.group.title, routeId: healthLeaf.group.routeId },
          { label: healthLeaf.leaf.title, routeId: 'health' },
        ],
        current: { label: report.title },
        icon: 'i-shield',
      }
    }
  }

  const nested = NESTED[routeId]
  if (nested) {
    const trail = trailForParent(nested.parent)
    // `v-pending`'s own `?job_id=` (`VPending.tsx`) names the specific restore job this worklist
    // narrows to, when it was reached from one — a real, working link, not an invented one; absent
    // (the workspace-wide worklist), the trail stops at Restore Jobs rather than a dead link.
    if (routeId === 'pending') {
      const jobId = new URLSearchParams(search).get('job_id')
      if (jobId) trail.push({ label: 'Restore Job', routeId: `jobs/restore/${jobId}` })
    }
    return { trail, current: { label: nested.title }, icon: nested.icon }
  }

  const prefixed = PREFIXED.find((p) => routeId.startsWith(p.prefix))
  if (prefixed) return { trail: trailForParent(prefixed.parent), current: { label: prefixed.title }, icon: prefixed.icon }

  // Not yet resolved (state-reached screens with no register row of their own, e.g. the prototype's
  // own `soon` placeholder, §27.5 C — plumbing, not product) — Home only, never an invented trail.
  return { trail: [], current: { label: HOME.title }, icon: 'i-home' }
}

/**
 * `div.crumbs` → `breadcrumbs`, `span.cur` → `breadcrumb-current` (process/08-COMPONENT-CATALOGUE.md).
 * **Built W180 — this element did not exist anywhere in `TopBar.tsx`.** The prototype's own `.crumbs`
 * sits in every `.topbar`, on every screen: a link
 * back to the product's own name, a middle trail (each segment a link to its own parent), and the
 * current screen (an icon plus its title, never a link). **`NESTED`/`PREFIXED` now cover the whole
 * of `design/27-ui-surface-and-navigation.md` §27.2's register** (extended 2026-09-26, live bug —
 * see `NESTED`'s own doc comment); only `soon` (explicitly not product, §27.5 C) still falls back
 * to Home.
 *
 * The prototype's own crumb link text is its product name, *"Bitbucket Cloud Backup and Granular
 * Restore"* — not used here. This app already settled on **"Bitbucket Data Protection"** as its one
 * product name (`AUTH_BRAND`, used on every auth screen and in `TopBar`'s own brand mark); using the
 * prototype's different, longer name here would introduce a third name for one product.
 */
export function Breadcrumbs({ onNavigate }: { onNavigate: (routeId: string) => void }) {
  const location = useLocation()
  const { trail, current, icon } = resolve(location.pathname, location.search)
  return (
    <div data-cmp="breadcrumbs" className="flex items-center gap-2 text-small text-tx2 min-w-0">
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault()
          onNavigate(HOME.routeId)
        }}
        className="underline whitespace-nowrap"
      >
        Bitbucket Data Protection
      </a>
      <span className="text-slate">/</span>
      {trail.map((c) => (
        <span key={c.label} className="flex items-center gap-2 whitespace-nowrap">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault()
              if (c.routeId) onNavigate(c.routeId)
            }}
            className="underline"
          >
            {c.label}
          </a>
          <span className="text-slate">/</span>
        </span>
      ))}
      <span data-cmp="breadcrumb-current" className="flex items-center gap-2 font-semibold text-tx whitespace-nowrap">
        <Icon name={icon} size="s" />
        {current.label}
      </span>
    </div>
  )
}
